import { useQuery } from '@tanstack/react-query';
import { CrewMerchantApi, CrewMerchantUi } from 'corso-types';
import { useMemo } from 'react';
import api from '~/api';
import { useStoreId } from '~/providers/StoreUserProvider';

type Search = Omit<
  CrewMerchantApi.RequestBody<'/:storeId/search', 'post'>,
  'take'
>;

const useSearchData = (search: Search) => {
  const storeId = useStoreId();
  const searchTermLength = search.filters.searchTerm?.length ?? 0;
  const isSearchTooLong =
    searchTermLength > CrewMerchantUi.MAX_SEARCH_TERM_LENGTH;

  const query = useQuery({
    queryKey: ['search', search, storeId],
    enabled: !isSearchTooLong,
    queryFn: async () =>
      api.store(storeId).search({
        take: 50,
        ...search,
        shouldUseOpenSearch: false,
      }),
    // prevents the table from flickering when updating
    placeholderData: (previous) => previous,
  });

  return useMemo(
    () => ({
      ...query,
      isSearchPending: query.isFetching,
      isSearchTooLong,
      data: query.data?.data ?? [],
      pageInfo: query.data?.pageInfo ?? { prevCursor: null, nextCursor: null },
    }),
    [query, isSearchTooLong],
  );
};

export default useSearchData;
